import { createContext, useCallback, useContext, useEffect, useState } from "react";

import { tokenBridge } from "./token-bridge";
import type { AuthContextValue, AuthProviderProps, SignInPayload } from "./types";

const AuthContext = createContext<AuthContextValue>({
  status: "signed-out",
  accountId: "",
  signIn: () => {},
  signOut: async () => {},
  refresh: async () => null,
});

export function CoreAuthProvider({ config, children }: AuthProviderProps) {
  const [status, setStatus] = useState<AuthContextValue["status"]>("loading");
  const [accountId, setAccountId] = useState<string>("");

  const signIn = useCallback(
    (payload: SignInPayload): void => {
      tokenBridge.setToken(payload.jwt);
      void tokenBridge.setRefreshToken(payload.refreshToken);
      void config.storage.setItem(config.accountKey, payload.accountId);
      setAccountId(payload.accountId);
      setStatus("signed-in");
    },
    [config],
  );

  const signOut = useCallback(async (): Promise<void> => {
    try {
      await config.onSignOut?.();
    } finally {
      tokenBridge.setToken(null);
      await tokenBridge.clearRefreshToken();
      await config.storage.removeItem(config.accountKey);
      setAccountId("");
      setStatus("signed-out");
    }
  }, [config]);

  const refresh = useCallback((): Promise<string | null> => tokenBridge.refresh(), []);

  useEffect(() => {
    tokenBridge.registerHandlers({ signOut });
  }, [signOut]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const pre = await config.onBeforeRestore?.();
        if (pre) {
          if (!cancelled) signIn(pre);
          return;
        }
      } catch (error) {
        console.warn("auth: onBeforeRestore failed", error);
      }

      const storedAccountId = (await config.storage.getItem<string>(config.accountKey)) ?? "";
      const jwt = await tokenBridge.refresh();
      if (cancelled) return;
      if (jwt && storedAccountId) {
        setAccountId(storedAccountId);
        setStatus("signed-in");
      } else {
        setStatus("signed-out");
      }
    })();
    return () => {
      cancelled = true;
    };
    // Bootstrap runs once on mount; signIn/config are stable for the app lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider value={{ status, accountId, signIn, signOut, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}
